import type { PortalInvoice, PortalInvoiceStatus } from "@/lib/api/billing";

export const INVOICE_STATUS: Record<PortalInvoiceStatus, { label: string; color: string }> = {
  unpaid: { label: "Unpaid", color: "blue" },
  paid: { label: "Paid", color: "green" },
  overdue: { label: "Overdue", color: "red" },
  credited: { label: "Credited", color: "gray" },
  credit_note: { label: "Credit note", color: "grape" },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-01" → "Oct 2026". Parsed by hand: `new Date("2026-10-01")` is UTC
 *  midnight, which is the previous day west of Greenwich. */
export function formatPeriod(period: string): string {
  const [year, month] = period.split("-").map(Number);
  if (!year || !month || month < 1 || month > 12) return period;
  return `${MONTHS[month - 1]} ${year}`;
}

/** "1250.00" → "1 250,00 kr" (Swedish formatting; the portal's operators invoice in SEK). */
export function formatAmount(amount: string, currency = "SEK"): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return amount;
  return new Intl.NumberFormat("sv-SE", { style: "currency", currency }).format(value);
}

export const PDF_UNAVAILABLE = "The PDF can't be fetched right now";
export const PDF_RATE_LIMITED = "Too many downloads — try again in a few minutes.";

export function pdfFilename(invoice: Pick<PortalInvoice, "id" | "kind" | "number">): string {
  const prefix = invoice.kind === "credit_note" ? "credit-note" : "invoice";
  return `${prefix}-${invoice.number ?? invoice.id}.pdf`;
}

export interface PdfDownloadDeps {
  fetchPdf: (id: number) => Promise<{ data: Blob }>;
  /** Hands the blob to the browser as a file. */
  save: (blob: Blob, filename: string) => void;
}

/** Fetch an invoice's PDF and save it. Resolves to an error message for the
 *  member, or null on success — never throws. */
export async function downloadInvoicePdf(
  invoice: Pick<PortalInvoice, "id" | "kind" | "number">,
  deps: PdfDownloadDeps,
): Promise<string | null> {
  try {
    const { data } = await deps.fetchPdf(invoice.id);
    deps.save(data, pdfFilename(invoice));
    return null;
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    return status === 429 ? PDF_RATE_LIMITED : PDF_UNAVAILABLE;
  }
}

/** Browser `save` for `downloadInvoicePdf`: a temporary object URL + anchor click. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoking right after click() can cancel the download in Safari/Firefox.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
